// Royalty-Free Open-Source SFX Generator using Web Audio API
// Synthesizes high-quality sound effects dynamically and returns playable Blob URLs

export function createSynthesizedSFX(type: string): string {
    const sampleRate = 44100
    let duration = 0.3 // seconds

    switch (type) {
        case 'whoosh': duration = 0.35; break
        case 'pop': duration = 0.08; break
        case 'click': duration = 0.04; break
        case 'ding': duration = 0.8; break
        case 'shutter': duration = 0.15; break
        case 'success': duration = 0.5; break
        case 'boom': duration = 0.6; break
        case 'sparkle': duration = 0.6; break
        default: duration = 0.2
    }

    const numSamples = Math.floor(sampleRate * duration)
    const audioBuffer = new Float32Array(numSamples)

    for (let i = 0; i < numSamples; i++) {
        const t = i / sampleRate
        const progress = i / numSamples

        if (type === 'whoosh') {
            // Filtered noise sweep
            const noise = Math.random() * 2 - 1
            const env = Math.sin(progress * Math.PI)
            audioBuffer[i] = noise * env * 0.4
        } else if (type === 'pop') {
            // Rapid pitch drop sine
            const freq = 600 * Math.exp(-progress * 25)
            const env = Math.exp(-progress * 30)
            audioBuffer[i] = Math.sin(2 * Math.PI * freq * t) * env * 0.6
        } else if (type === 'click') {
            // Crisp impulse click
            const freq = 1200 * Math.exp(-progress * 40)
            const env = Math.exp(-progress * 50)
            audioBuffer[i] = Math.sin(2 * Math.PI * freq * t) * env * 0.5
        } else if (type === 'ding') {
            // Bright bell chime (C6 + E6)
            const env = Math.exp(-progress * 5)
            const tone1 = Math.sin(2 * Math.PI * 1046.5 * t)
            const tone2 = Math.sin(2 * Math.PI * 1318.5 * t) * 0.5
            audioBuffer[i] = (tone1 + tone2) * env * 0.35
        } else if (type === 'shutter') {
            // Dual shutter click
            const click1 = (t < 0.04) ? (Math.random() * 2 - 1) * Math.exp(-t * 80) : 0
            const click2 = (t > 0.07 && t < 0.11) ? (Math.random() * 2 - 1) * Math.exp(-(t - 0.07) * 80) : 0
            audioBuffer[i] = (click1 + click2) * 0.5
        } else if (type === 'success') {
            // 3-note arpeggio (C5, E5, G5)
            let freq = 523.25
            if (progress > 0.33) freq = 659.25
            if (progress > 0.66) freq = 783.99
            const env = Math.exp(-((progress * 3) % 1) * 6)
            audioBuffer[i] = Math.sin(2 * Math.PI * freq * t) * env * 0.35
        } else if (type === 'boom') {
            // Sub bass impact
            const freq = 120 * Math.exp(-progress * 6)
            const env = Math.exp(-progress * 4)
            audioBuffer[i] = Math.sin(2 * Math.PI * freq * t) * env * 0.6
        } else if (type === 'sparkle') {
            // Arpeggiated shimmer
            const freq = 1200 + Math.sin(progress * Math.PI * 16) * 600
            const env = Math.sin(progress * Math.PI)
            audioBuffer[i] = Math.sin(2 * Math.PI * freq * t) * env * 0.3
        } else {
            audioBuffer[i] = Math.sin(2 * Math.PI * 440 * t) * Math.exp(-progress * 10) * 0.3
        }
    }

    // Convert Float32Array to 16-bit PCM WAV Blob
    const wavBuffer = createWavBlob(audioBuffer, sampleRate)
    return URL.createObjectURL(wavBuffer)
}

function createWavBlob(samples: Float32Array, sampleRate: number): Blob {
    const buffer = new ArrayBuffer(44 + samples.length * 2)
    const view = new DataView(buffer)

    /* RIFF identifier */
    writeString(view, 0, 'RIFF')
    /* RIFF chunk length */
    view.setUint32(4, 36 + samples.length * 2, true)
    /* RIFF type */
    writeString(view, 8, 'WAVE')
    /* format chunk identifier */
    writeString(view, 12, 'fmt ')
    /* format chunk length */
    view.setUint32(16, 16, true)
    /* sample format (raw PCM) */
    view.setUint16(20, 1, true)
    /* channel count (mono) */
    view.setUint16(22, 1, true)
    /* sample rate */
    view.setUint32(24, sampleRate, true)
    /* byte rate (sampleRate * blockAlign) */
    view.setUint32(28, sampleRate * 2, true)
    /* block align (channelCount * bytesPerSample) */
    view.setUint16(32, 2, true)
    /* bits per sample */
    view.setUint16(34, 16, true)
    /* data chunk identifier */
    writeString(view, 36, 'data')
    /* data chunk length */
    view.setUint32(40, samples.length * 2, true)

    // Write 16-bit PCM samples
    let offset = 44
    for (let i = 0; i < samples.length; i++, offset += 2) {
        const s = Math.max(-1, Math.min(1, samples[i]))
        view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7FFF, true)
    }

    return new Blob([buffer], { type: 'audio/wav' })
}

function writeString(view: DataView, offset: number, string: string) {
    for (let i = 0; i < string.length; i++) {
        view.setUint8(offset + i, string.charCodeAt(i))
    }
}
